import { Suspense, lazy } from "react";
import React from "react";
import {
  Card,
  CardActions,
  CardContent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import type { LottieProps } from "react-lottie-player";
import { captureException } from "@/observability/sentry";

const Lottie = lazy(() => import("react-lottie-player"));

// Lottie rendering crashed in the past, so we specifically handle it here:
// https://webshare-proxy.sentry.io/issues/7010088700/?project=4503931616100352
// https://gitlab.com/unified-proxy/frontend/proxycontrolpaneldashboard-container/-/issues/2749
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    const lottierError = new Error(
      "Problem with Lottie. This is handled gracefully."
    );
    lottierError.cause = error;
    captureException(lottierError, {
      extra: {
        errorInfo: JSON.stringify(errorInfo),
      },
    });
    console.error("Lottie Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

function Visual({
  lottieOptions,
  imageSrc,
  imageAlt,
  visualSize = 100,
}: {
  lottieOptions?: LottieProps;
  imageSrc?: string;
  imageAlt?: string;
  visualSize?: number;
}) {
  const theme = useTheme();
  return (
    <Stack sx={{ pt: 2, pb: 2 }} justifyContent="center">
      {lottieOptions ? (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <Lottie
              {...lottieOptions}
              style={{ width: visualSize, height: visualSize }}
            />
          </Suspense>
        </ErrorBoundary>
      ) : null}
      {imageSrc ? (
        <img
          loading="lazy"
          decoding="async"
          alt={imageAlt || "image"}
          src={`${imageSrc}${theme.palette.mode === "dark" ? "-dark" : ""}.svg`}
          width={visualSize}
          height={visualSize}
          style={{ margin: "0 auto" }}
        />
      ) : null}
    </Stack>
  );
}

export default function InfoCard({
  title,
  description,
  backgroundColor,
  elevation = 0,
  subtitle,
  lottieOptions,
  imageSrc,
  imageAlt,
  visualSize,
  testId,
  children,
}: {
  title?: string;
  description?: string;
  elevation?: number;
  backgroundColor?: string;
  subtitle?: string;
  lottieOptions?: LottieProps;
  imageAlt?: string;
  imageSrc?: string;
  visualSize?: number;
  testId?: string;
  children?: React.ReactNode;
}) {
  const theme = useTheme();
  const defaultBackgroundColor = theme.palette.background.paper;

  return (
    <Card
      sx={{ p: 2, backgroundColor: backgroundColor || defaultBackgroundColor }}
      data-testid={testId}
      elevation={elevation}
    >
      <CardContent
        sx={{
          pt: 0,
          pr: 0,
          pb: 2,
          pl: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Visual
          lottieOptions={lottieOptions}
          imageSrc={imageSrc}
          imageAlt={imageAlt}
          visualSize={visualSize}
        />
        <Stack
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{ maxWidth: "520px" }}
        >
          {title ? (
            <Typography variant="h5" fontWeight="bold" textAlign="center">
              {title}
            </Typography>
          ) : null}
          {subtitle || description ? (
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              style={{ whiteSpace: "pre-line" }}
            >
              {subtitle ? <span>{`${subtitle}\n`}</span> : null}
              {description ? <span>{description}</span> : null}
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
      <CardActions sx={{ display: "flex", justifyContent: "center" }}>
        <Stack direction="row" spacing={2} sx={{ minWidth: 0 }}>
          {children}
        </Stack>
      </CardActions>
    </Card>
  );
}
